import { useMutationState } from '@tanstack/react-query';
import { AppWindow, MessageCircle } from 'lucide-react';
import { type MutableRefObject, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useBootstrapConfig } from '@/Domain/components/App/BootstrapProvider';
import { PreviewContext } from '@/Domain/components/PreviewProvider';
import {
  RadioGroupSegmented,
  RadioGroupSegmentedItem,
} from '@/Domain/components/Shared/RadioGroup/RadioGroup';
import { cn } from '@/Domain/utils/cn';
import { getCurrentScenarioId } from '@/context';

const WIDGET_URL = import.meta.env.VITE_WIDGET_URL || 'https://bubble.quickchat.ai';
const WIDGET_ORIGIN = new URL(WIDGET_URL).origin;

const TRANSITION_DURATION = 250;

type ViewMode = 'bubble' | 'embed';

const VIEW_MODE_OPTIONS = [
  { value: 'bubble' as const, label: 'Widget', icon: MessageCircle },
  { value: 'embed' as const, label: 'Embed', icon: AppWindow },
];

type NewWidgetProps = {
  showViewModeToggle?: boolean;
  fixedViewType?: string;
  defaultViewMode?: ViewMode;
  className?: string;
  source?: string;
  customParams?: Record<string, unknown>;
  // Preview-only override forwarded to widget.html as the `colorScheme` URL
  // param. `system` (or undefined) leaves the host's prefers-color-scheme as
  // the source of truth.
  colorScheme?: 'system' | 'light' | 'dark';
};

const NewWidget = ({
  showViewModeToggle = true,
  fixedViewType,
  defaultViewMode = 'bubble',
  className,
  source,
  customParams,
  colorScheme,
}: NewWidgetProps) => {
  const [iframeKey, setIframeKey] = useState(0);
  const [viewMode, setViewMode] = useState<ViewMode>(defaultViewMode);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const { apiServerUrl } = useBootstrapConfig();
  const host = `https://${apiServerUrl}`;
  const scenarioId = getCurrentScenarioId();

  const params = new URLSearchParams({
    scenarioId,
    viewType: fixedViewType ?? viewMode,
    host,
    ...(source ? { source } : {}),
    ...(colorScheme && colorScheme !== 'system' ? { colorScheme } : {}),
  });
  if (customParams && Object.keys(customParams).length > 0) {
    params.set('params', JSON.stringify(customParams));
  }

  const handleViewModeChange = (mode: ViewMode) => {
    if (mode === viewMode || isTransitioning) return;

    setIsTransitioning(true);

    setTimeout(() => {
      setViewMode(mode);
      setIframeKey((prev) => prev + 1);

      setTimeout(() => {
        setIsTransitioning(false);
      }, TRANSITION_DURATION);
    }, TRANSITION_DURATION);
  };

  // Keep a ref to handleViewModeChange to avoid stale closure in the effect below
  const handleViewModeChangeRef = useRef(handleViewModeChange);
  handleViewModeChangeRef.current = handleViewModeChange;

  const previewContext = useContext(PreviewContext);
  const localRef = useRef<HTMLIFrameElement>(null);
  const contextIframeRef = previewContext?.iframe;

  // Callback ref that sets both the local ref (guaranteed to work) and the
  // context ref (used by setConfiguration in PreviewProvider).
  const mergedRef = useCallback(
    (node: HTMLIFrameElement | null) => {
      localRef.current = node;
      if (
        contextIframeRef &&
        typeof contextIframeRef === 'object' &&
        'current' in contextIframeRef
      ) {
        (contextIframeRef as MutableRefObject<HTMLIFrameElement | null>).current = node;
      }
    },
    [contextIframeRef],
  );

  const prevBubbleReqRef = useRef(0);
  useEffect(() => {
    const reqId = previewContext?.bubbleViewRequestId ?? 0;
    if (reqId > prevBubbleReqRef.current) {
      prevBubbleReqRef.current = reqId;
      handleViewModeChangeRef.current('bubble');
    }
  }, [previewContext?.bubbleViewRequestId]);

  // Failsafe: after each successful mutation, tell the widget to refetch config
  // from the server. Uses the local ref (not context) for reliability.
  const refreshConfiguration = useCallback(() => {
    localRef.current?.contentWindow?.postMessage(
      { type: 'quickchat_refresh_configuration' },
      WIDGET_ORIGIN,
    );
  }, []);

  const mutationSuccessCount = useMutationState({
    filters: { mutationKey: ['widget-settings'], status: 'success' },
    select: (mutation) => mutation.state.submittedAt,
  });

  useEffect(() => {
    if (mutationSuccessCount.length > 0) {
      refreshConfiguration();
    }
  }, [mutationSuccessCount.length, mutationSuccessCount.at(-1), refreshConfiguration]);

  // Drag is handled INSIDE the iframe (see widget's View.tsx — when `source=preview`
  // it applies the transform directly to the launcher element). Translating the whole
  // iframe here would move the entire fake page + background, not just the launcher.

  return (
    <div className={`flex flex-col items-center gap-4 w-full h-full ${className ?? ''}`}>
      {showViewModeToggle && (
        <RadioGroupSegmented
          value={viewMode}
          onValueChange={(value) => handleViewModeChange(value as ViewMode)}
        >
          {VIEW_MODE_OPTIONS.map(({ value, label, icon: Icon }) => (
            <RadioGroupSegmentedItem
              key={value}
              value={value}
              id={`view-mode-${value}`}
              className="flex items-center gap-1.5"
            >
              <Icon className="size-3.5" />
              {label}
            </RadioGroupSegmentedItem>
          ))}
        </RadioGroupSegmented>
      )}

      <div className="w-full flex-1 relative">
        <iframe
          ref={mergedRef}
          // Combine the manual iframeKey (bumped on view-mode toggle) with
          // colorScheme so flipping the preview's light/dark switch remounts
          // the iframe and re-runs widget.html's URL-param parsing.
          key={`${iframeKey}-${colorScheme ?? 'system'}`}
          src={`${WIDGET_URL}/widget.html?${params.toString()}`}
          className={cn(
            'w-full h-full border-0 bg-transparent transition-all duration-250 ease-in-out',
            fixedViewType ? 'rounded-none' : 'rounded-3xl',
            isTransitioning ? 'opacity-0 scale-[0.8]' : 'opacity-100 scale-100',
          )}
          allow="microphone"
        />
      </div>
    </div>
  );
};
export default NewWidget;
